"use client";

import { createContext, useContext, type MutableRefObject } from "react";
import type { TeamsGalleryTeamOverlayHandle } from "@/components/ui/Cteamsgallery";
import type {
  TeamModalContextValue,
  TeamModalOpenState,
} from "./team-modal-types";

export const TeamModalContext = createContext<TeamModalContextValue | null>(
  null,
);

export function useTeamModal(): TeamModalContextValue {
  const context = useContext(TeamModalContext);
  if (context == null) {
    throw new Error("useTeamModal must be used within a TeamModalProvider");
  }
  return context;
}

/**
 * The provider↔host seam. The host renders the one overlay; these are the
 * pieces of the provider's machinery it needs beyond the public API: the open
 * snapshot, a per-open generation (the overlay remounts per open so its
 * initial-mode props are read fresh), the overlay's imperative handle slot,
 * and the overlay's report channels that keep the tracked mode honest.
 */
export interface TeamModalHostBridgeValue {
  openState: TeamModalOpenState | null;
  openGeneration: number;
  overlayHandleRef: MutableRefObject<TeamsGalleryTeamOverlayHandle | null>;
  /** The overlay finished its collapse — settle the state to Closed. */
  onOverlayClosed: () => void;
  /** The embedded preflight (deploy) mounted/unmounted inside the overlay. */
  onPreflightActiveChange: (active: boolean) => void;
  /** The details editor activated/deactivated inside the overlay. */
  onEditorActiveChange: (active: boolean) => void;
  /** An edit-mode open fell back to the regular state (the viewer lacks the
   *  matching action) — the tracked mode follows the surface. */
  onEditIntentFallback: () => void;
  /** The overlay retargeted itself onto another team IN PLACE — a create
   *  landing, a save-copy landing, the linked-copy "Show", or a saved-copies
   *  pick. The tracked subject swaps to that team on the same open
   *  generation, so the controller always names the team on screen. */
  onSubjectRetargeted: (input: { teamId: string }) => void;
}

export const TeamModalHostBridgeContext =
  createContext<TeamModalHostBridgeValue | null>(null);

export function useTeamModalHostBridge(): TeamModalHostBridgeValue {
  const bridge = useContext(TeamModalHostBridgeContext);
  if (bridge == null) {
    throw new Error(
      "useTeamModalHostBridge must be used within a TeamModalProvider",
    );
  }
  return bridge;
}
